import React, { useState, useCallback } from 'react';
import axios from 'axios';

const C = {
	bg:      '#0a0a1a',
	surface: '#0e0e1c',
	border:  'rgba(255,255,255,0.08)',
	primary: '#7c6deb',
	teal:    '#46bcaa',
	textPri: '#ffffff',
	textSec: 'rgba(255,255,255,0.55)',
	textMeta:'rgba(255,255,255,0.32)',
} as const;

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.justknow.co.in';

const inputStyle: React.CSSProperties = {
	background: 'rgba(255,255,255,0.04)',
	border: `1px solid ${C.border}`,
	borderRadius: '10px',
	padding: '12px 16px',
	color: C.textPri,
	fontSize: '14px',
	outline: 'none',
	width: '100%',
	transition: 'border-color 0.2s',
	fontFamily: 'inherit',
};

const ContactPage: React.FC = () => {
	const [form,   setForm]   = useState({ name: '', email: '', subject: '', message: '' });
	const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
	const [error,  setError]  = useState('');

	const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
	}, []);

	const handleSubmit = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		setStatus('sending');
		setError('');
		try {
			await axios.post(`${API_BASE}/api/public/contact`, form);
			setStatus('success');
			setForm({ name: '', email: '', subject: '', message: '' });
		} catch (err: any) {
			setStatus('error');
			setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
		}
	}, [form]);

	return (
		<div style={{
			position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
			overflowY: 'auto', background: C.bg,
			fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
			color: C.textPri, zIndex: 9000,
		}}>

			{/* Nav */}
			<nav style={{
				position: 'sticky', top: 0, zIndex: 100,
				background: 'rgba(10,10,26,0.9)', backdropFilter: 'blur(20px)',
				borderBottom: '1px solid rgba(255,255,255,0.06)',
				padding: '0 24px',
			}}>
				<div style={{ maxWidth: 860, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<a href='/'>
						<img src='/logobanner.png' alt='Just Know' style={{ height: 32, objectFit: 'contain' }} />
					</a>
					<a href='/' style={{ color: C.textSec, fontSize: 14, textDecoration: 'none' }}>← Back to Home</a>
				</div>
			</nav>

			{/* Page content */}
			<div style={{ maxWidth: 860, margin: '0 auto', padding: '56px 24px 80px' }}>

				{/* Header */}
				<div style={{ marginBottom: 48 }}>
					<div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(124,109,235,0.1)', border: '1px solid rgba(124,109,235,0.2)', borderRadius: '50px', padding: '5px 14px', marginBottom: '20px' }}>
						<span style={{ color: C.primary, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Get In Touch</span>
					</div>
					<h1 style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 900, letterSpacing: '-1.5px', marginBottom: 14 }}>Contact Us</h1>
					<p style={{ color: C.textSec, fontSize: 15, lineHeight: 1.75, maxWidth: 560 }}>
						Have a question, feedback, or a partnership inquiry? We'd love to hear from you.
						Fill in the form below or reach us directly at the details listed.
					</p>
				</div>

				{/* Two-column layout */}
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 48, alignItems: 'start' }}>

					{/* Left — company info */}
					<div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>Company</div>
							<p style={{ color: C.textPri, fontSize: 14, fontWeight: 600, lineHeight: 1.6, margin: 0 }}>JGS Media and Software Solutions<br />Private Limited</p>
						</div>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>Email</div>
							<a href='mailto:contact@justknow.co.in' style={{ color: C.primary, fontSize: 14, textDecoration: 'none', fontWeight: 500 }}>
								contact@justknow.co.in
							</a>
						</div>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>Support</div>
							<a href='mailto:support@justknow.co.in' style={{ color: C.primary, fontSize: 14, textDecoration: 'none', fontWeight: 500 }}>
								support@justknow.co.in
							</a>
						</div>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>App</div>
							<p style={{ color: C.textSec, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
								Available on Google Play Store and Apple App Store
							</p>
						</div>

						<div>
							<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>Response Time</div>
							<p style={{ color: C.textSec, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
								We typically respond within 1–2 business days.
							</p>
						</div>

					</div>

					{/* Right — contact form */}
					<form onSubmit={handleSubmit}
						style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`, borderRadius: 18, padding: 'clamp(24px, 4vw, 36px)', display: 'flex', flexDirection: 'column', gap: 18 }}>

						<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
							<div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
								<label style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>Your Name *</label>
								<input name='name' value={form.name} onChange={handleChange} required
									placeholder='e.g. Ravi Kumar' style={inputStyle}
									onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
									onBlur={e => { e.target.style.borderColor = C.border; }} />
							</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
								<label style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>Email Address *</label>
								<input name='email' type='email' value={form.email} onChange={handleChange} required
									placeholder='you@example.com' style={inputStyle}
									onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
									onBlur={e => { e.target.style.borderColor = C.border; }} />
							</div>
						</div>

						<div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
							<label style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>Subject *</label>
							<select name='subject' value={form.subject} onChange={handleChange} required
								style={{ ...inputStyle, background: 'rgba(14,14,28,0.95)', cursor: 'pointer', color: form.subject ? C.textPri : C.textMeta }}
								onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
								onBlur={e => { e.target.style.borderColor = C.border; }}>
								<option value='' disabled>Select a subject</option>
								<option value='General Inquiry'>General Inquiry</option>
								<option value='App Feedback'>App Feedback</option>
								<option value='Report a Bug'>Report a Bug</option>
								<option value='Partnership / Advertising'>Partnership / Advertising</option>
								<option value='Media / Press'>Media / Press</option>
								<option value='Account Support'>Account Support</option>
								<option value='Other'>Other</option>
							</select>
						</div>

						<div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
							<label style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>Message *</label>
							<textarea name='message' value={form.message} onChange={handleChange} required rows={6}
								placeholder='Tell us how we can help...'
								style={{ ...inputStyle, resize: 'vertical' }}
								onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
								onBlur={e => { e.target.style.borderColor = C.border; }} />
						</div>

						{status === 'success' && (
							<div style={{ background: 'rgba(70,188,170,0.1)', border: '1px solid rgba(70,188,170,0.25)', borderRadius: 9, padding: '13px 16px', color: C.teal, fontSize: 14, textAlign: 'center' }}>
								✓ Message sent! We'll get back to you within 1–2 business days.
							</div>
						)}
						{status === 'error' && (
							<div style={{ background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.22)', borderRadius: 9, padding: '13px 16px', color: '#ff6b6b', fontSize: 14, textAlign: 'center' }}>
								{error}
							</div>
						)}

						<button type='submit' disabled={status === 'sending'}
							style={{ background: status === 'sending' ? 'rgba(124,109,235,0.45)' : C.primary, border: 'none', borderRadius: 10, padding: '13px 28px', color: '#fff', fontSize: 14, fontWeight: 700, cursor: status === 'sending' ? 'not-allowed' : 'pointer', transition: 'all 0.25s' }}
							onMouseEnter={e => { if (status !== 'sending') (e.currentTarget as HTMLElement).style.opacity = '0.85'; }}
							onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}>
							{status === 'sending' ? 'Sending…' : 'Send Message'}
						</button>

					</form>
				</div>

				{/* Footer */}
				<div style={{ borderTop: `1px solid ${C.border}`, marginTop: 64, paddingTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
					<span style={{ color: C.textMeta, fontSize: 13 }}>© {new Date().getFullYear()} Just Know. All rights reserved.</span>
					<div style={{ display: 'flex', gap: 20 }}>
						<a href='/privacy-policy' style={{ color: C.textMeta, fontSize: 13, textDecoration: 'none' }}>Privacy Policy</a>
						<a href='/delete-account' style={{ color: C.textMeta, fontSize: 13, textDecoration: 'none' }}>Delete Account</a>
					</div>
				</div>
			</div>

			<style>{`
				@media (max-width: 640px) {
					.contact-grid { grid-template-columns: 1fr !important; }
				}
			`}</style>
		</div>
	);
};

export default ContactPage;
